/**
 * The demo campaigns store: one per tenant fixture, held for the life of the
 * `MockApiClient`, so a campaign created or edited in the session survives
 * navigation. It is seeded from the tenant fixture's campaigns — the same ones
 * the global filter bar offers — against the shared clock, and it enforces the
 * rules the API does (a valid landing path, a unique promo code, no tracking on
 * an archived campaign), so the demo cannot do what the product would refuse.
 *
 * Figures come from `performance.ts`, never from this file.
 */

import {
  AUDIENCE_SEGMENTS,
  CAMPAIGN_OBJECTIVES,
  CAMPAIGN_STATUS_LABELS,
  CREATIVE_SLOTS,
  activationStatus,
  emptyMetrics,
  isChannelType,
  isOwnedChannel,
  sumMetrics,
  type Campaign,
  type CampaignActivity,
  type CampaignChannel,
  type CampaignCreative,
  type CampaignDetail,
  type CampaignDraft,
  type CampaignListItem,
  type CampaignPatch,
  type CampaignPerformance,
  type CampaignTracking,
  type CampaignWebsite,
  type ChannelKind,
  type PerformanceMetrics,
  type ChannelPatch,
  type ChannelType,
  type CreativePatch,
  type PromoCodeMode,
  type SaveIntent,
  type TrackingGenerateInput,
  type TrackingPatch,
} from '@/api/campaigns'
import { qrSvg } from '@/features/marketing/campaigns/qr'
import {
  buildTrackingUrl,
  defaultTrackingDraft,
  landingPathError,
  normalisePromoCode,
  promoCodeFor,
  shortCodeFor,
  slugify,
} from '@/features/marketing/campaigns/tracking'
import { addDays, dayCount, resolvePeriod, toDateKey, type GlobalFilters } from '@/lib/filters'

import { DemoDataset } from '../generators/engine'
import { unitNoise } from '../random'
import type { CampaignFixture, TenantFixture } from '../tenants/types'
import { performanceRow, seededPerformance } from './performance'

interface StoredChannel extends CampaignChannel {
  /** The tenant fixture channel whose figures this channel shares, for seeded campaigns. */
  fixtureChannelId: string | null
}

interface StoredCampaign {
  campaign: Campaign
  channels: StoredChannel[]
  creatives: CampaignCreative[]
  tracking: CampaignTracking[]
  activity: CampaignActivity[]
}

interface MintOptions {
  landingPath: string | null
  utmSource?: string
  utmMedium?: string
  utmContent?: string
  shortLink: boolean
  qrCode: boolean
  promoMode: PromoCodeMode
  promoCode?: string
}

/** A refusal the API would also give; the screens show its message. */
export class CampaignValidationError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'CampaignValidationError'
  }
}

/** A stored channel without the store's own bookkeeping. */
function publicChannel(channel: StoredChannel): CampaignChannel {
  const { id, campaignId, name, channelType, kind, status, budget, spend, providerRef, createdAt, updatedAt } = channel
  return { id, campaignId, name, channelType, kind, status, budget, spend, providerRef, createdAt, updatedAt }
}

function at(date: string, time = '09:00:00'): string {
  return `${date}T${time}.000Z`
}

export class CampaignsStore {
  private readonly stored: StoredCampaign[] = []
  private readonly dataset: DemoDataset
  private counter = 0

  constructor(
    private readonly tenant: TenantFixture,
    now: Date,
  ) {
    this.dataset = new DemoDataset(tenant, now)
    // One at a time: each seed checks its promo codes against the campaigns seeded before it.
    for (const fixture of tenant.campaigns) this.stored.push(this.seed(fixture))
  }

  // --- reads -------------------------------------------------------------------

  websites(): CampaignWebsite[] {
    return this.tenant.websites.map((website) => ({
      id: website.id,
      name: website.name,
      origin: `https://${website.name}`,
      environment: website.environment,
    }))
  }

  list(dataset: DemoDataset, filters: GlobalFilters): CampaignListItem[] {
    const figures = seededPerformance(dataset, filters)
    return this.stored.map((entry) => ({
      campaign: structuredClone(entry.campaign),
      channels: entry.channels.map(({ id, name, channelType, kind }) => ({ id, name, channelType, kind })),
      totals: sumMetrics(this.rows(entry, figures).map((row) => row.metrics)),
    }))
  }

  detail(id: string): CampaignDetail | null {
    const entry = this.find(id)
    if (!entry) return null
    return structuredClone({
      campaign: entry.campaign,
      channels: entry.channels.map(publicChannel),
      creatives: entry.creatives,
      tracking: entry.tracking,
      activity: [...entry.activity].sort((a, b) => b.at.localeCompare(a.at) || b.id.localeCompare(a.id)),
    })
  }

  performance(id: string, dataset: DemoDataset, filters: GlobalFilters): CampaignPerformance {
    const entry = this.require(id)
    const period = resolvePeriod(filters, dataset.now)
    const rows = this.rows(entry, seededPerformance(dataset, filters)).map(({ channel, metrics }) =>
      performanceRow(entry.campaign.id, channel, metrics),
    )
    return {
      campaignId: entry.campaign.id,
      currency: entry.campaign.currency,
      start: period.current.from,
      end: period.current.to,
      nonProduction: filters.environments.some((environment) => environment !== 'production'),
      channels: rows,
      totals: sumMetrics(rows),
    }
  }

  qrSvg(campaignId: string, trackingId: string): string {
    const row = this.require(campaignId).tracking.find((candidate) => candidate.id === trackingId)
    if (!row?.qrUrl) throw new CampaignValidationError('No QR code has been generated for this tracking.')
    return qrSvg(row.qrUrl, { title: `QR code for ${row.qrUrl}` })
  }

  // --- writes ------------------------------------------------------------------

  create(draft: CampaignDraft, intent: SaveIntent, now: Date): CampaignDetail {
    const name = draft.name.trim()
    if (!name) throw new CampaignValidationError('Give the campaign a name.')
    if (!draft.objective) throw new CampaignValidationError('Choose an objective.')
    if (!this.websites().some((website) => website.id === draft.websiteId)) {
      throw new CampaignValidationError('Choose the website this campaign sends people to.')
    }
    if (draft.startsAt && draft.endsAt && draft.endsAt < draft.startsAt) {
      throw new CampaignValidationError('The end date must not be before the start date.')
    }
    if (intent === 'launch' && draft.channels.length === 0) {
      throw new CampaignValidationError('Add at least one channel before launching.')
    }

    const today = toDateKey(now)
    const timestamp = now.toISOString()
    this.counter += 1
    const id = `camp_new_${this.counter}`
    const status = intent === 'draft' ? 'draft' : activationStatus(null, draft.startsAt, today)
    const campaign: Campaign = {
      id,
      websiteId: draft.websiteId,
      name,
      slug: this.uniqueSlug(slugify(name)),
      description: draft.description.trim() || null,
      objective: draft.objective,
      customObjective: draft.objective === 'custom' ? draft.customObjective.trim() || null : null,
      status,
      competitionIds: [...draft.competitionIds],
      startsAt: draft.startsAt || null,
      endsAt: draft.endsAt || null,
      totalBudget: draft.totalBudget,
      currency: this.tenant.currency,
      audience: structuredClone(draft.audience),
      automations: structuredClone(draft.automations),
      otherAutomationRefs: [],
      createdAt: timestamp,
      updatedAt: timestamp,
    }
    const entry: StoredCampaign = { campaign, channels: [], creatives: [], tracking: [], activity: [] }
    this.log(entry, timestamp, intent === 'draft' ? 'Campaign saved as a draft' : `Campaign launched — ${CAMPAIGN_STATUS_LABELS[status].toLowerCase()}`)

    // Validate every channel's tracking before anything is stored, so a refusal leaves nothing behind.
    const pendingCodes = new Set<string>()
    for (const channel of draft.channels) {
      const error = landingPathError(channel.tracking.landingPath)
      if (error) throw new CampaignValidationError(`${channel.name}: ${error}`)
      if (channel.tracking.promoMode === 'existing') {
        const code = normalisePromoCode(channel.tracking.promoCode)
        if (!code) throw new CampaignValidationError(`${channel.name}: that is not a valid promo code.`)
        if (pendingCodes.has(code) || this.promoCodeTaken(code, null)) {
          throw new CampaignValidationError(`${channel.name}: that promo code is already in use.`)
        }
        pendingCodes.add(code)
      }
    }

    draft.channels.forEach((channelDraft, index) => {
      const channel: StoredChannel = {
        id: `${id}_ch${index + 1}`,
        campaignId: id,
        name: channelDraft.name.trim() || channelDraft.channelType,
        channelType: channelDraft.channelType,
        kind: channelDraft.kind,
        status: intent === 'draft' ? 'draft' : 'active',
        budget: isOwnedChannel(channelDraft.channelType) ? null : channelDraft.budget,
        spend: null,
        providerRef: null,
        createdAt: timestamp,
        updatedAt: timestamp,
        fixtureChannelId: null,
      }
      entry.channels.push(channel)
      this.log(entry, timestamp, `${channel.name} channel added`)

      const { headline, body, assetUrl } = channelDraft.creative
      if (headline.trim() || body.trim() || assetUrl.trim()) {
        const slot = CREATIVE_SLOTS[channel.channelType]
        entry.creatives.push({
          id: `${channel.id}_cr1`,
          campaignId: id,
          channelId: channel.id,
          name: `${channel.name} — ${slot.label}`,
          creativeType: slot.creativeType,
          status: channel.status,
          headline: headline.trim() || null,
          body: body.trim() || null,
          assetUrl: assetUrl.trim() || null,
          createdAt: timestamp,
          updatedAt: timestamp,
        })
      }

      const tracking = channelDraft.tracking
      entry.tracking.push(
        this.mint(entry, channel, timestamp, {
          landingPath: tracking.landingPath || null,
          utmSource: tracking.utmSource,
          utmMedium: tracking.utmMedium,
          utmContent: tracking.utmContent,
          shortLink: tracking.shortLink,
          qrCode: tracking.qrCode,
          promoMode: tracking.promoMode,
          promoCode: tracking.promoCode,
        }),
      )
      this.log(entry, timestamp, `Tracking generated for ${channel.name}`)
    })

    this.stored.unshift(entry)
    return this.detail(id) as CampaignDetail
  }

  update(id: string, patch: CampaignPatch, now: Date): CampaignDetail {
    const entry = this.require(id)
    const { campaign } = entry
    const timestamp = now.toISOString()
    if (patch.name !== undefined) {
      const name = patch.name.trim()
      if (!name) throw new CampaignValidationError('A campaign needs a name.')
      if (name !== campaign.name) this.log(entry, timestamp, `Renamed to “${name}”`)
      campaign.name = name
    }
    if (patch.description !== undefined) campaign.description = patch.description?.trim() || null
    const startsAt = patch.startsAt !== undefined ? patch.startsAt : campaign.startsAt
    const endsAt = patch.endsAt !== undefined ? patch.endsAt : campaign.endsAt
    if (startsAt && endsAt && endsAt < startsAt) throw new CampaignValidationError('The end date must not be before the start date.')
    campaign.startsAt = startsAt
    campaign.endsAt = endsAt
    if (patch.status !== undefined) {
      // A draft or paused campaign activated before its start date is scheduled, whoever asks.
      const status = patch.status === 'active' ? activationStatus(campaign.status, campaign.startsAt, toDateKey(now)) : patch.status
      if (status !== campaign.status) {
        campaign.status = status
        this.log(entry, timestamp, `Status changed to ${CAMPAIGN_STATUS_LABELS[status].toLowerCase()}`)
      }
    }
    if (patch.totalBudget !== undefined) {
      if (patch.totalBudget !== null && patch.totalBudget < 0) throw new CampaignValidationError('A budget cannot be negative.')
      campaign.totalBudget = patch.totalBudget
      this.log(entry, timestamp, 'Total budget updated')
    }
    if (patch.competitionIds !== undefined) campaign.competitionIds = [...patch.competitionIds]
    if (patch.audience !== undefined) {
      campaign.audience = structuredClone(patch.audience)
      this.log(entry, timestamp, 'Audience updated')
    }
    if (patch.automations !== undefined) {
      campaign.automations = structuredClone(patch.automations)
      this.log(entry, timestamp, 'Automation triggers updated')
    }
    campaign.updatedAt = timestamp
    return this.detail(id) as CampaignDetail
  }

  updateChannel(campaignId: string, channelId: string, patch: ChannelPatch, now: Date): CampaignDetail {
    const entry = this.require(campaignId)
    const channel = entry.channels.find((candidate) => candidate.id === channelId)
    if (!channel) throw new CampaignValidationError('That channel is not part of this campaign.')
    const timestamp = now.toISOString()
    for (const value of [patch.budget, patch.spend]) {
      if (value !== undefined && value !== null && value < 0) throw new CampaignValidationError('An amount cannot be negative.')
    }
    if (patch.budget !== undefined) {
      channel.budget = patch.budget
      this.log(entry, timestamp, `${channel.name} budget updated`)
    }
    if (patch.spend !== undefined) channel.spend = patch.spend
    if (patch.status !== undefined && patch.status !== channel.status) {
      channel.status = patch.status
      this.log(entry, timestamp, `${channel.name} ${patch.status === 'active' ? 'resumed' : `set to ${patch.status}`}`)
    }
    channel.updatedAt = timestamp
    return this.detail(campaignId) as CampaignDetail
  }

  updateCreative(campaignId: string, creativeId: string, patch: CreativePatch, now: Date): CampaignDetail {
    const entry = this.require(campaignId)
    const creative = entry.creatives.find((candidate) => candidate.id === creativeId)
    if (!creative) throw new CampaignValidationError('That creative is not part of this campaign.')
    if (patch.assetUrl && !/^https?:\/\/\S+$/.test(patch.assetUrl)) {
      throw new CampaignValidationError('An asset URL starts with http:// or https://.')
    }
    if (patch.headline !== undefined) creative.headline = patch.headline?.trim() || null
    if (patch.body !== undefined) creative.body = patch.body?.trim() || null
    if (patch.assetUrl !== undefined) creative.assetUrl = patch.assetUrl?.trim() || null
    creative.updatedAt = now.toISOString()
    this.log(entry, creative.updatedAt, `${creative.name} edited`)
    return this.detail(campaignId) as CampaignDetail
  }

  /** Idempotent like the API: codes already allocated are kept; newly requested ones are added. */
  generateTracking(campaignId: string, input: TrackingGenerateInput, now: Date): CampaignDetail {
    const entry = this.require(campaignId)
    if (entry.campaign.status === 'archived') throw new CampaignValidationError('An archived campaign cannot mint tracking.')
    const channel = entry.channels.find((candidate) => candidate.id === input.channelId)
    if (!channel) throw new CampaignValidationError('That channel is not part of this campaign.')
    const timestamp = now.toISOString()
    const existing = entry.tracking.find((row) => row.channelId === channel.id && row.creativeId === null)
    const minted = this.mint(entry, channel, timestamp, {
      landingPath: existing?.landingPath ?? null,
      shortLink: input.shortLink,
      qrCode: input.qrCode,
      promoMode: input.generatePromoCode ? 'generate' : 'none',
    })
    if (existing) {
      existing.shortCode ??= minted.shortCode
      existing.shortUrl ??= minted.shortUrl
      existing.qrCode ??= minted.qrCode
      existing.qrUrl ??= minted.qrUrl
      existing.promoCode ??= minted.promoCode
      existing.updatedAt = timestamp
    } else {
      entry.tracking.push(minted)
    }
    this.log(entry, timestamp, `Tracking generated for ${channel.name}`)
    return this.detail(campaignId) as CampaignDetail
  }

  updateTracking(campaignId: string, trackingId: string, patch: TrackingPatch, now: Date): CampaignDetail {
    const entry = this.require(campaignId)
    const row = entry.tracking.find((candidate) => candidate.id === trackingId)
    if (!row) throw new CampaignValidationError('That tracking is not part of this campaign.')
    const channel = entry.channels.find((candidate) => candidate.id === row.channelId)
    const timestamp = now.toISOString()
    if (patch.landingPath !== undefined) {
      const path = patch.landingPath || null
      const error = path ? landingPathError(path) : null
      if (error) throw new CampaignValidationError(error)
      row.landingPath = path
      row.trackingUrl = buildTrackingUrl(this.origin(entry.campaign), path, {
        utmSource: row.utmSource,
        utmMedium: row.utmMedium,
        utmCampaign: row.utmCampaign,
        utmContent: row.utmContent,
        trackingId: row.id,
      })
      this.log(entry, timestamp, `${channel?.name ?? 'Channel'} landing page changed`)
    }
    if (patch.status !== undefined && patch.status !== row.status) {
      row.status = patch.status
      this.log(entry, timestamp, `${channel?.name ?? 'Channel'} tracking ${patch.status === 'active' ? 'resumed' : patch.status}`)
    }
    row.updatedAt = timestamp
    return this.detail(campaignId) as CampaignDetail
  }

  // --- internals ---------------------------------------------------------------

  private find(id: string): StoredCampaign | undefined {
    return this.stored.find((entry) => entry.campaign.id === id)
  }

  private require(id: string): StoredCampaign {
    const entry = this.find(id)
    if (!entry) throw new CampaignValidationError('Campaign not found.')
    return entry
  }

  private rows(entry: StoredCampaign, figures: Map<string, Map<string, PerformanceMetrics>>) {
    const byChannel = figures.get(entry.campaign.id)
    return entry.channels.map((channel) => ({
      channel,
      metrics: (channel.fixtureChannelId ? byChannel?.get(channel.fixtureChannelId) : undefined) ?? emptyMetrics(),
    }))
  }

  private log(entry: StoredCampaign, timestamp: string, message: string): void {
    entry.activity.push({ id: `${entry.campaign.id}:act${entry.activity.length + 1}`, at: timestamp, message })
  }

  private uniqueSlug(base: string): string {
    const taken = new Set(this.stored.map((entry) => entry.campaign.slug))
    let slug = base
    for (let suffix = 2; taken.has(slug); suffix += 1) slug = `${base}-${suffix}`
    return slug
  }

  private origin(campaign: Campaign): string {
    const website = this.websites().find((candidate) => candidate.id === campaign.websiteId) ?? this.websites()[0]
    return website?.origin ?? 'https://example.com'
  }

  /** Short links resolve on the tenant's Insights host (pivot doc §7, assumption 6). */
  private linkHost(campaign: Campaign): string {
    return `insights.${new URL(this.origin(campaign)).hostname.replace(/^www\./, '')}`
  }

  private promoCodeTaken(code: string, exceptTrackingId: string | null): boolean {
    return this.stored.some((entry) => entry.tracking.some((row) => row.promoCode === code && row.id !== exceptTrackingId))
  }

  private mint(entry: StoredCampaign, channel: CampaignChannel, timestamp: string, options: MintOptions): CampaignTracking {
    const { campaign } = entry
    const id = `trk_${channel.id}`
    const landingPath = options.landingPath || null
    const pathError = landingPath ? landingPathError(landingPath) : null
    if (pathError) throw new CampaignValidationError(`${channel.name}: ${pathError}`)
    const defaults = defaultTrackingDraft(channel.channelType, channel.kind, channel.name)
    const utmSource = options.utmSource || defaults.utmSource
    const utmMedium = options.utmMedium || defaults.utmMedium
    const utmContent = options.utmContent || defaults.utmContent
    const host = this.linkHost(campaign)
    const shortCode = options.shortLink ? shortCodeFor(this.tenant.id, id, 'link') : null
    const qrCode = options.qrCode ? shortCodeFor(this.tenant.id, id, 'qr') : null

    let promoCode: string | null = null
    if (options.promoMode === 'existing') {
      promoCode = normalisePromoCode(options.promoCode ?? '')
      if (!promoCode) throw new CampaignValidationError(`${channel.name}: that is not a valid promo code.`)
      if (this.promoCodeTaken(promoCode, id)) throw new CampaignValidationError(`${channel.name}: that promo code is already in use.`)
    } else if (options.promoMode === 'generate') {
      for (let attempt = 0; !promoCode || this.promoCodeTaken(promoCode, id); attempt += 1) {
        promoCode = promoCodeFor(campaign.slug, this.tenant.id, id, String(attempt))
      }
    }

    return {
      id,
      campaignId: campaign.id,
      channelId: channel.id,
      creativeId: null,
      utmSource,
      utmMedium,
      utmCampaign: campaign.slug,
      utmContent,
      landingPath,
      trackingUrl: buildTrackingUrl(this.origin(campaign), landingPath, {
        utmSource,
        utmMedium,
        utmCampaign: campaign.slug,
        utmContent,
        trackingId: id,
      }),
      shortCode,
      shortUrl: shortCode ? `https://${host}/r/${shortCode}` : null,
      qrCode,
      qrUrl: qrCode ? `https://${host}/r/${qrCode}` : null,
      promoCode,
      status: 'active',
      createdAt: timestamp,
      updatedAt: timestamp,
    }
  }

  /** A fixture campaign as a full record, every choice a pure function of the tenant seed and the clock. */
  private seed(fixture: CampaignFixture): StoredCampaign {
    const { tenant, dataset } = this
    const noise = (...key: string[]) => unitNoise(tenant.seed, 'campaign', fixture.id, ...key)
    const today = dataset.today
    const startsAt = addDays(today, -(10 + Math.floor(noise('start') * 18)))
    const endsAt = addDays(today, 5 + Math.floor(noise('end') * 25))
    const createdAt = at(addDays(startsAt, -3))
    const website = tenant.websites.find((candidate) => candidate.environment === 'production') ?? tenant.websites[0]
    const open = dataset
      .openOn(today)
      .sort((a, b) => noise('competition', a.id) - noise('competition', b.id))
      .slice(0, noise('competitions') > 0.5 ? 2 : 1)
    const include = AUDIENCE_SEGMENTS[Math.floor(noise('segment') * AUDIENCE_SEGMENTS.length)]?.id ?? 'seg_returning_customers'

    const campaign: Campaign = {
      id: fixture.id,
      websiteId: website?.id ?? '',
      name: fixture.name,
      slug: slugify(fixture.name),
      description: null,
      objective: CAMPAIGN_OBJECTIVES[Math.floor(noise('objective') * 7)] ?? 'drive_entries',
      customObjective: null,
      status: 'active',
      competitionIds: open.map((competition) => competition.id),
      startsAt,
      endsAt,
      totalBudget: null,
      currency: tenant.currency,
      audience: {
        include: [include],
        exclude: include === 'seg_recent_winners' ? [] : ['seg_recent_winners'],
        geography: [],
      },
      automations: [],
      otherAutomationRefs: [],
      createdAt,
      updatedAt: at(startsAt),
    }
    const entry: StoredCampaign = { campaign, channels: [], creatives: [], tracking: [], activity: [] }
    this.log(entry, createdAt, 'Campaign saved as a draft')

    const days = dayCount(startsAt, endsAt)
    for (const channelId of fixture.channels) {
      const source = tenant.channels.find((candidate) => candidate.id === channelId)
      if (!source) continue
      const channelType: ChannelType = isChannelType(channelId) ? channelId : 'custom'
      const kind: ChannelKind = source.kind
      const channel: StoredChannel = {
        id: `${fixture.id}_${channelId}`,
        campaignId: fixture.id,
        name: source.name,
        channelType,
        kind,
        status: 'active',
        budget:
          isOwnedChannel(channelType) || source.dailySpend === 0 ? null : Math.round((source.dailySpend * days * 0.6) / 10) * 10,
        spend: null,
        providerRef: null,
        createdAt,
        updatedAt: createdAt,
        fixtureChannelId: channelId,
      }
      entry.channels.push(channel)
      this.log(entry, createdAt, `${channel.name} channel added`)

      for (const creative of tenant.creatives.filter((item) => item.campaignId === fixture.id && item.channel === channelId)) {
        const slot = CREATIVE_SLOTS[channelType]
        entry.creatives.push({
          id: creative.id,
          campaignId: fixture.id,
          channelId: channel.id,
          name: creative.name,
          creativeType: slot.creativeType,
          status: 'active',
          headline: slot.fields.headline ? creative.name : null,
          body: slot.fields.headline ? null : creative.name,
          assetUrl: null,
          createdAt,
          updatedAt: createdAt,
        })
      }

      const offline = kind === 'offline'
      const competition = open[0]
      entry.tracking.push(
        this.mint(entry, channel, createdAt, {
          landingPath: competition ? `/competitions/${slugify(competition.name)}` : null,
          shortLink: true,
          qrCode: offline,
          promoMode: offline ? 'generate' : 'none',
        }),
      )
      this.log(entry, createdAt, `Tracking generated for ${channel.name}`)
    }

    const planned = entry.channels.reduce((sum, channel) => sum + (channel.budget ?? 0), 0)
    campaign.totalBudget = planned > 0 ? Math.ceil((planned * 1.15) / 50) * 50 : null
    campaign.automations = entry.channels.some((channel) => channel.kind === 'offline')
      ? [{ trigger: 'promo_code_first_use', action: 'notify_team' }]
      : [{ trigger: 'competition_sold_80', action: 'send_push' }]
    this.log(entry, at(startsAt), 'Status changed to active')
    return entry
  }
}
